import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControlLabel,
  Alert,
  IconButton,
  Avatar,
  Tabs,
  Tab,
  Dialog as MuiDialog,
} from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ContactApiService from "../services/ContactApiService";
import MessageTemplateService from "../services/MessageTemplateService";
import TagService from "../services/TagService";

const CreateBroadcastDialog = ({ open, onClose, onSave, editBroadcast }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    broadcastName: "",
    selectedTemplate: "",
    scheduleDate: new Date(),
    sendNow: false,
    selectedContacts: [],
  });
  const [templateVariables, setTemplateVariables] = useState({ header: {}, body: {}, footer: {} });
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectionType, setSelectionType] = useState("contacts");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    ContactApiService.getContactsDetails({ limit: 10000}).then((res) => setContacts(res));
    MessageTemplateService.getAllTemplates().then((res) => setApprovedTemplates(res));
    ContactApiService.getGroupList({ limit: 1000 }).then((res) => setGroups(res));
    TagService.getAllTags({ limit: 1000 }).then((res) => setTags(res));
  }, []);
  useEffect(() => {
    if (open && editBroadcast) {
      setFormData({
        broadcastName: editBroadcast.attrs.broadcastName || "",
        selectedTemplate: editBroadcast.attrs.selectedTemplate || "",
        scheduleDate: editBroadcast.attrs.scheduleDate ? new Date(editBroadcast.attrs.scheduleDate) : new Date(),
        sendNow: !!editBroadcast.attrs.sendNow,
        selectedContacts: editBroadcast.attrs.selectedContacts || [],
        selectedGroups: editBroadcast.attrs.selectedGroups || [],
        selectedTags: editBroadcast.attrs.selectedTags || [],
      });
      setTemplateVariables({
        header: (editBroadcast.attrs.templateVariables && editBroadcast.attrs.templateVariables.header) || {},
        body: (editBroadcast.attrs.templateVariables && editBroadcast.attrs.templateVariables.body) || {},
        footer: (editBroadcast.attrs.templateVariables && editBroadcast.attrs.templateVariables.footer) || {},
      });
      if (editBroadcast.attrs.selectedContacts) {
        const selectedIds = editBroadcast.attrs.selectedContacts.map(c => c.attrs && c.attrs.id);
        setContacts((prev) =>
          prev.map((contact) =>
            selectedIds.includes(contact.attrs.id)
              ? { ...contact, isSelected: true }
              : { ...contact, isSelected: false }
          )
        );
      }
      if (editBroadcast.attrs.selectedGroups) {
        const selectedIds = editBroadcast.attrs.selectedGroups.map(c => c.attrs && c.attrs.id);
        setGroups((prev) =>
          prev.map((group) =>
            selectedIds.includes(group.attrs.id)
              ? { ...group, isSelected: true }
              : { ...group, isSelected: false }
          )
        );
      }

      if (editBroadcast.attrs.selectedTags) {
        const selectedIds = editBroadcast.attrs.selectedTags.map(c => c.attrs && c.attrs.id);
        setTags((prev) =>
          prev.map((group) =>
            selectedIds.includes(group.attrs.id)
              ? { ...group, isSelected: true }
              : { ...group, isSelected: false }
          )
        );
      }

      setActiveStep(0);
    } else if (open && !editBroadcast) {
      setFormData({
        broadcastName: "",
        selectedTemplate: "",
        scheduleDate: new Date(),
        sendNow: false,
        selectedContacts: [],
        selectedGroups: [],
        selectedTags: [],
      });
      setTemplateVariables({ header: {}, body: {}, footer: {} });
      setContacts((prev) => prev.map((c) => ({ ...c, isSelected: false })));
      setGroups((prev) => prev.map((g) => ({ ...g, isSelected: false })));
      setTags((prev) => prev.map((t) => ({ ...t, isSelected: false })));
      setActiveStep(0);
    }
  }, [open, editBroadcast]);

  const steps = ["Broadcast Setup", "Contact Selection"];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
    if (field === "selectedTemplate") {
      const selected = approvedTemplates.find(t => t.getId() === value);
      let variableSamples = { header: {}, body: {}, footer: {} };
      try {
        variableSamples = selected ? JSON.parse(selected.attrs.variableSamples) : { header: {}, body: {}, footer: {} };
      } catch (e) {}
      setTemplateVariables({
        header: Object.fromEntries(Object.keys(variableSamples.header || {}).map(k => [k, ""])),
        body: Object.fromEntries(Object.keys(variableSamples.body || {}).map(k => [k, ""])),
        footer: Object.fromEntries(Object.keys(variableSamples.footer || {}).map(k => [k, ""])),
      });
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.broadcastName.trim()) {
      newErrors.broadcastName = "Broadcast name is required";
    }

    if (!formData.selectedTemplate) {
      newErrors.selectedTemplate = "Please select a template";
    }

    if (!formData.sendNow && !formData.scheduleDate) {
      newErrors.scheduleDate = "Please select a schedule date";
    }
    const selected = approvedTemplates.find(t => t.getId() === formData.selectedTemplate);
    let variableSamples = { header: {}, body: {}, footer: {} };
    try {
      variableSamples = selected ? JSON.parse(selected.attrs.variableSamples) : { header: {}, body: {}, footer: {} };
    } catch (e) {}
    ["header", "body", "footer"].forEach(section => {
      if (variableSamples[section]) {
        Object.keys(variableSamples[section]).forEach(key => {
          if (!templateVariables[section][key] || !templateVariables[section][key].trim()) {
            newErrors[`var_${section}_${key}`] = `Please enter value for variable {{${key}}} in ${section}`;
          }
        });
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (activeStep === 0 && validateStep1()) {
      setActiveStep(1);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handleSelectionTypeChange = (event, newValue) => {
    setSelectionType(newValue);
  };

  const handleConfirmClear = () => {
    if (pendingSelection === "contacts") {
      setGroups((prev) => prev.map((g) => ({ ...g, isSelected: false })));
    } else if (pendingSelection === "groups") {
      setContacts((prev) => prev.map((c) => ({ ...c, isSelected: false })));
    } else if (pendingSelection === "groupOrContact"){
      setTags((prev) => prev.map((t) => ({ ...t, isSelected: false })));
    }
    setConfirmDialogOpen(false);
    setPendingSelection(null);
  };
  const handleCancelClear = () => {
    setConfirmDialogOpen(false);
    setPendingSelection(null);
  };

  const handleContactSelection = (contactId) => {
    if (selectedGroupsCount > 0) {
      setPendingSelection("contacts");
      setConfirmDialogOpen(true);
      return;
    }
    setContacts((prev) =>
      prev.map((contact) =>
        contact.attrs.id === contactId
          ? { ...contact, isSelected: !contact.isSelected }
          : contact
      )
    );
  };
  const handleGroupSelection = (groupId) => {
    if (selectedContactsCount > 0) {
      setPendingSelection("groups");
      setConfirmDialogOpen(true);
      return;
    }
    setGroups((prev) =>
      prev.map((group) =>
        group.attrs.id === groupId
          ? { ...group, isSelected: !group.isSelected }
          : group
      )
    );
  };

  const handleTagsSelection = (tagId) => {
    if (selectedContactsCount > 0 || selectedGroupsCount > 0) {
      setPendingSelection("groupOrContact");
      setConfirmDialogOpen(true);
      return;
    }
    setTags((prev) =>
      prev.map((tag) =>
        tag.attrs.id === tagId
          ? { ...tag, isSelected: !tag.isSelected }
          : tag
      )
    );
  };
  const handleSelectAll = (event) => {
    if (selectedGroupsCount > 0) {
      setPendingSelection("contacts");
      setConfirmDialogOpen(true);
      return;
    }
    const isChecked = event.target.checked;
    setContacts((prev) => prev.map((contact) => ({ ...contact, isSelected: isChecked })));
  };
  const handleSelectAllGroup = (event) => {
    if (selectedContactsCount > 0) {
      setPendingSelection("groups");
      setConfirmDialogOpen(true);
      return;
    }
    const isChecked = event.target.checked;
    setGroups((prev) => prev.map((group) => ({ ...group, isSelected: isChecked })));
  };
  const handleSelectAllTags = (event) => {
    if (selectedContactsCount > 0 || selectedGroupsCount > 0) {
      setPendingSelection("groupOrContact");
      setConfirmDialogOpen(true);
      return;
    }
    const isChecked = event.target.checked;
    setTags((prev) => prev.map((tag) => ({ ...tag, isSelected: isChecked })));
  };

  const handleSave = () => {
    const selectedContacts = contacts.filter((contact) => contact.isSelected);
    const selectedGroups = groups.filter((group) => group.isSelected);
    const selectedTags = tags.filter((tag) => tag.isSelected);
    const broadcastData = {
      ...formData,
      selectedContacts: selectedContacts,
      selectedGroups: selectedGroups,
      selectedTags: selectedTags,
      templateVariables,
    };

    console.log("Saving broadcast:", broadcastData);
    onSave(broadcastData);
    onClose();
  };

  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      broadcastName: "",
      selectedTemplate: "",
      scheduleDate: new Date(),
      sendNow: false,
      selectedContacts: [],
      selectedGroups: [],
      selectedTags: [],
    });
    setErrors({});
    setSearchQuery("");
    setContacts((prev) =>
      prev.map((contact) => ({ ...contact, isSelected: false }))
    );
    setGroups((prev) =>
      prev.map((group) => ({ ...group, isSelected: false }))
    );
    setTags((prev) =>
      prev.map((tag) => ({ ...tag, isSelected: false }))
    );
    onClose();
  };

  const getTagColor = (tag) => {
    return tag || "#757575";
  };
console.log('contacts', contacts);

  const filteredContacts = contacts.filter(
    (contact) =>
      contact.attrs.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.attrs.phoneNumber.includes(searchQuery)
  );
  const filteredGroups = groups.filter(
    (group) =>
      group.attrs.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredTags = tags.filter(
    (tag) =>
      tag.attrs.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedContactsCount = contacts.filter(
    (contact) => contact.isSelected
  ).length;
  const selectedGroupsCount = groups.filter(
    (group) => group.isSelected
  ).length;
  const selectedTagsCount = tags.filter(
    (tag) => tag.isSelected
  ).length;
  const isAllSelected =
    contacts.length > 0 && selectedContactsCount === contacts.length;
  const isIndeterminate =
    selectedContactsCount > 0 && selectedContactsCount < contacts.length;
    const isAllGroupSelected =
    groups.length > 0 && selectedGroupsCount === groups.length;
  const isGroupIndeterminate =
    selectedGroupsCount > 0 && selectedGroupsCount < groups.length;
    const isAllTagSelected =
    tags.length > 0 && selectedTagsCount === tags.length;
  const isTagIndeterminate =
    selectedTagsCount > 0 && selectedTagsCount < tags.length;

  const renderStep1 = () => {
    const selected = approvedTemplates.find(t => t.getId() === formData.selectedTemplate);
    let variableSamples = { header: {}, body: {}, footer: {} };
    try {
      variableSamples = selected ? JSON.parse(selected.attrs.variableSamples) : { header: {}, body: {}, footer: {} };
    } catch (e) {}

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#333", mb: 2, fontFamily: 'Poppins, sans-serif'}}
            >
              Broadcast Details
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Broadcast Name"
                value={formData.broadcastName}
                onChange={(e) =>
                  handleInputChange("broadcastName", e.target.value)
                }
                error={!!errors.broadcastName}
                helperText={errors.broadcastName}
                fullWidth
                size="small"
                placeholder="Enter broadcast name"
              />

              <FormControl
                fullWidth
                size="small"
                error={!!errors.selectedTemplate}
              >
                <InputLabel>Select Template</InputLabel>
                <Select
                  value={formData.selectedTemplate}
                  onChange={(e) =>
                    handleInputChange("selectedTemplate", e.target.value)
                  }
                  label="Select Template"
                >
                  {approvedTemplates.map((template) => (
                    <MenuItem key={template.getId()} value={template.getId()}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {template.getTemplateName()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {template.getCategory()} • {template.getLanguage()}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.selectedTemplate && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {errors.selectedTemplate}
                  </Typography>
                )}
              </FormControl>

              {/* Variable input fields for header/body/footer */}
              {["header", "body", "footer"].map((section) => (
                variableSamples[section] && Object.keys(variableSamples[section]).length > 0 && (
                  <Box key={section} sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      {section.charAt(0).toUpperCase() + section.slice(1)} Variables
                    </Typography>
                    {Object.keys(variableSamples[section]).map((key) => (
                      <TextField
                        key={key}
                        label={`Value for {{${key}}}`}
                        value={templateVariables[section][key] || ""}
                        onChange={(e) =>
                          setTemplateVariables((prev) => ({
                            ...prev,
                            [section]: {
                              ...prev[section],
                              [key]: e.target.value,
                            },
                          }))
                        }
                        error={!!errors[`var_${section}_${key}`]}
                        helperText={errors[`var_${section}_${key}`] || `Enter value for {{${key}}}`}
                        fullWidth
                        size="small"
                      />
                    ))}
                  </Box>
                )
              ))}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.sendNow}
                    onChange={(e) =>
                      handleInputChange("sendNow", e.target.checked)
                    }
                    size="small"
                  />
                }
                label="Send Now"
                sx={{ alignSelf: "flex-start", fontFamily: 'Poppins, sans-serif' }}
              />

              {!formData.sendNow && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Schedule Date & Time"
                    value={formData.scheduleDate}
                    onChange={(newValue) =>
                      handleInputChange("scheduleDate", newValue)
                    }
                    minutesStep={60}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        error={!!errors.scheduleDate}
                        helperText={errors.scheduleDate}
                      />
                    )}
                    minDateTime={new Date()}
                  />
                </LocalizationProvider>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderStep2 = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Tabs value={selectionType} onChange={handleSelectionTypeChange} sx={{ mb: 2 }}>
        <Tab label="Contacts" value="contacts" />
        <Tab label="Groups" value="groups" />
        <Tab label="Tags" value="tags" />
      </Tabs>
      {selectionType === "contacts" ? (
        <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
              Select Recipients
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "#9e9e9e", mr: 0.5, fontSize: 18 }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: "#fafafa",
                    border: "1px solid #e0e0e0",
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor: "#d0d0d0",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#fff",
                      borderColor: "#1976d2",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
              />
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ backgroundColor: "#f5f5f5" }}>
                        <Checkbox
                          indeterminate={isIndeterminate}
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          sx={{ color: "#666" }}
                        />
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Phone Number
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Tags
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredContacts?.map((contact) => (
                      <TableRow key={contact.attrs.id} sx={{ "&:hover": { backgroundColor: "#f8f9fa" } }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={contact.isSelected}
                            onChange={() => handleContactSelection(contact.attrs.id)}
                            sx={{ color: "#666" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: "#1976d2", fontSize: "0.875rem" }}>
                              <PersonIcon />
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {contact?.attrs.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{contact.attrs.phoneNumber}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {contact?.attrs.tags.map((tag) => (
                              <Chip
                                key={tag.getId()}
                                label={tag.getName()}
                                size="small"
                                sx={{
                                  backgroundColor: `${getTagColor(tag.getColor())}15`,
                                  color: getTagColor(tag.getColor()),
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {selectedContactsCount > 0 && (
                <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                  <Typography variant="body2">
                    {selectedContactsCount} contact(s) selected for broadcast
                  </Typography>
                </Alert>
              )}
            </Box>
          </CardContent>
        </Card>
      ) : selectionType === "groups" ? (
        <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
              Select Groups
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                placeholder="Search groups..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "#9e9e9e", mr: 0.5, fontSize: 18 }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: "#fafafa",
                    border: "1px solid #e0e0e0",
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor: "#d0d0d0",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#fff",
                      borderColor: "#1976d2",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
              />
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ backgroundColor: "#f5f5f5" }}>
                        <Checkbox
                          indeterminate={isGroupIndeterminate}
                          checked={isAllGroupSelected}
                          onChange={handleSelectAllGroup}
                          sx={{ color: "#666" }}
                        />
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Description
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Contacts Count
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredGroups?.map((group) => (
                      <TableRow key={group.attrs.id} sx={{ "&:hover": { backgroundColor: "#f8f9fa" } }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={group.isSelected}
                            onChange={() => handleGroupSelection(group.attrs.id)}
                            sx={{ color: "#666" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {group?.attrs.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{group.attrs.description}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{group.attrs.contactsCount}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {selectedGroupsCount > 0 && (
                <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                  <Typography variant="body2">
                    {selectedGroupsCount} group(s) selected for broadcast
                  </Typography>
                </Alert>
              )}
            </Box>
          </CardContent>
        </Card>
      ) : selectionType === "tags" && (
        <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
              Select Tags
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                placeholder="Search tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "#9e9e9e", mr: 0.5, fontSize: 18 }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: "#fafafa",
                    border: "1px solid #e0e0e0",
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor: "#d0d0d0",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#fff",
                      borderColor: "#1976d2",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
              />
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ backgroundColor: "#f5f5f5" }}>
                        <Checkbox
                          indeterminate={isTagIndeterminate}
                          checked={isAllTagSelected}
                          onChange={handleSelectAllTags}
                          sx={{ color: "#666" }}
                        />
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Description
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Contacts Count
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTags?.map((tag) => (
                      <TableRow key={tag.attrs.id} sx={{ "&:hover": { backgroundColor: "#f8f9fa" } }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={tag.isSelected}
                            onChange={() => handleTagsSelection(tag.attrs.id)}
                            sx={{ color: "#666" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {tag?.attrs.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{tag.attrs.description}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{tag.attrs.contactsCount}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {selectedTagsCount > 0 && (
                <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                  <Typography variant="body2">
                    {selectedTagsCount} tag(s) selected for broadcast
                  </Typography>
                </Alert>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
      <MuiDialog open={confirmDialogOpen} onClose={handleCancelClear}>
        <DialogTitle>Clear Selection?</DialogTitle>
        <DialogContent>
          <Typography>
            {pendingSelection === "contacts"
              ? "Selecting contacts will clear selected groups. Continue?"
              : pendingSelection === "groups" ? "Selecting groups will clear selected contacts. Continue?"
            : pendingSelection === "groupOrContact" ? "Selecting tags will clear selected contacts and groups. Continue?" : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClear}>Cancel</Button>
          <Button onClick={handleConfirmClear} color="primary">Continue</Button>
        </DialogActions>
      </MuiDialog>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: "1px solid #e0e0e0" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#333", fontFamily: 'Poppins, sans-serif' }}>
            Create Broadcast
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 4 }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          {activeStep === 0 ? renderStep1() : renderStep2()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0", gap: 1 }}>
        {activeStep === 1 && (
          <Button
            onClick={handleBack}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              px: 3,
            }}
          >
            Back
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        {activeStep === 0 ? (
          <Button
            onClick={handleNext}
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              px: 3,
              background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
              },
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SendIcon />}
            disabled={selectionType === 'contacts' ? selectedContactsCount === 0 : selectionType === 'groups' ? selectedGroupsCount === 0 : selectionType === 'tags' ? selectedTagsCount === 0 : false}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              px: 3,
              background: "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #45a049 0%, #3d8b40 100%)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "#999",
                fontFamilys: 'Poppins, sans-serif',
              },
            }}
          >
            Create Broadcast ({selectionType === 'contacts' ? selectedContactsCount : selectionType === 'groups' ? selectedGroupsCount : selectionType === 'tags' ? selectedTagsCount : 0})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateBroadcastDialog;
